"use client";

import { useTranslations } from "next-intl";
import { useEffect, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { Link } from "@/i18n/navigation";
import { useBracket } from "@/entities/bracket/api/hooks";
import {
  isCompletedStatus,
  matchStatusBadgeVariant,
  matchStatusLabel,
} from "@/entities/bracket/lib/match-status";
import { findMatchTeam } from "@/entities/match/lib/match-team-link";
import { getMatchPanels } from "@/entities/match/model/match-panels";
import { useUserMatchSide } from "@/entities/match/model/use-user-match-side";
import type { TournamentTeamWithDraftLink } from "@/entities/match/lib/match-team-link";
import { useMatchDisputesByMatch } from "@/entities/match-dispute";
import { useTournamentStore } from "@/entities/tournament/model/tournament-store";
import { MatchGameTeamStats } from "@/widgets/bracket/match-game-team-stats";
import { MatchLaneMatchupsSection } from "@/widgets/bracket/match-lane-matchups-section";
import { MatchBehaviorProfile } from "@/widgets/bracket/match-behavior-profile";
import { MatchTimelineFeed } from "@/widgets/bracket/match-timeline-feed";
import { CreateDisputeForm } from "@/features/match-dispute/ui/create-dispute-form";
import { DisputeStatusBadge } from "@/features/match-dispute/ui/dispute-status-badge";
import { MatchChecklist } from "@/features/match-checklist/ui/match-checklist";
import { MatchPreDraftCard } from "@/features/match-predraft-card/ui/match-predraft-card";
import { MatchChampionPoolsSection } from "@/features/match-champion-pools/ui/match-champion-pools-section";
import { MatchDraftResult } from "@/features/match-draft-result/ui/match-draft-result";
import { MatchSideVetoBlock } from "@/features/match-veto/ui/match-side-veto-block";
import { canManageTournament } from "@/entities/tournament/lib/permissions";
import { CaptainResultCard } from "@/features/tournament/captain-result-card";
import { ReplayConfirmCard } from "@/features/tournament/replay-confirm-card";
import { useUserProfile } from "@/hooks/useAuth";
import { useGetTournamentsIdTeams } from "@/shared/api/generated-kubb/hooks/useGetTournamentsIdTeams";
import { useLcuStatus } from "@/shared/lib/hooks/use-lcu-status";
import {
  getMatchChecklist,
  resolveMatchUserRole,
} from "@/entities/bracket/lib/match-lifecycle";
import { checkIsTauri } from "@/shared/lib/tauri";
import { cn } from "@/shared/lib/utils";
import { Badge } from "@/shared/ui/data-display/badge";
import { Skeleton } from "@/shared/ui/data-display/skeleton";
import { MatchLiveBoard } from "@/widgets/match-live-board";
import { DownloadAppBanner } from "@/widgets/tauri/download-app-banner";
import { LcuStatusPanel } from "@/widgets/tauri/lcu-status-panel";

import { MatchTeamStyles } from "./ui/match-team-styles";
import { TeamLineup } from "./ui/team-lineup";
import { formatTimeOnlyMsk } from "@/shared/lib/msk-time";

export interface MatchOverviewProps {
  tournamentId: string;
  matchId: string;
  className?: string;
}

/**
 * Содержимое страницы матча.
 *
 * Самостоятельный виджет: идентификаторы приходят пропсами, роутер не читается,
 * собственных отступов страницы нет — тот же блок должен без правок вставляться
 * в модалку над сеткой.
 *
 * Главное на экране — кто против кого: две команды, составы с ролями и Riot ID,
 * счёт и статус. Всё, что питается десктоп-приложением (телеметрия, LCU-панель,
 * чек-лист), показывается только внутри приложения — в вебе эти блоки были
 * гарантированно пустыми и делали страницу похожей на сломанную.
 */
export function MatchOverview({
  tournamentId,
  matchId,
  className,
}: MatchOverviewProps) {
  const tUi = useTranslations("Ui");
  const t = useTranslations("Match");
  const tDispute = useTranslations("MatchDispute");

  const { currentTournament: tournament, fetchTournamentById } =
    useTournamentStore(
      useShallow((state) => ({
        currentTournament: state.currentTournament,
        fetchTournamentById: state.fetchTournamentById,
      })),
    );

  const { data: apiBracket, refetch: refetchBracket } =
    useBracket(tournamentId);
  const { data: userProfile } = useUserProfile();
  const { data: tournamentTeamsResponse } = useGetTournamentsIdTeams(
    tournamentId,
    { query: { enabled: Boolean(tournamentId) } },
  );

  const match = apiBracket?.matches.find((m) => m.id === matchId);
  const matchCompleted = isCompletedStatus(match?.status);
  // KAN-1676: пулы RandomDraft выдаёт организатор/staff турнира. Кнопку не
  // показываем игрокам и зрителям — у них только просмотр.
  const canManagePools = canManageTournament(
    userProfile?.id ?? undefined,
    userProfile?.primaryPlatformRole ?? undefined,
    tournament,
  );

  // Диспуты бывают только по завершённому матчу — на остальных запрос гарантированно пустой.
  const { data: disputes = [] } = useMatchDisputesByMatch(matchId, {
    enabled: matchCompleted,
  });

  const isTauriHere = checkIsTauri();
  const { status: lcuStatus } = useLcuStatus();

  useEffect(() => {
    if (tournamentId) fetchTournamentById(tournamentId);
  }, [tournamentId, fetchTournamentById]);

  const tournamentTeams = useMemo<TournamentTeamWithDraftLink[]>(() => {
    const raw =
      (tournamentTeamsResponse as { data?: unknown } | undefined)?.data ??
      tournamentTeamsResponse;
    return Array.isArray(raw) ? (raw as TournamentTeamWithDraftLink[]) : [];
  }, [tournamentTeamsResponse]);

  const bracketTeamA = match?.teamAId
    ? apiBracket?.teams.find((x) => x.id === match.teamAId)
    : null;
  const bracketTeamB = match?.teamBId
    ? apiBracket?.teams.find((x) => x.id === match.teamBId)
    : null;

  const teamA = findMatchTeam(tournamentTeams, match?.teamAId);
  const teamB = findMatchTeam(tournamentTeams, match?.teamBId);

  const {
    side: userSide,
    isCaptain,
    teamACaptainId,
    teamBCaptainId,
    teamAMemberIds,
    teamBMemberIds,
  } = useUserMatchSide({
    userId: userProfile?.id ?? null,
    teamAId: match?.teamAId ?? null,
    teamBId: match?.teamBId ?? null,
    tournamentTeams,
  });

  const tournamentType = ((tournament as { type?: string | null } | null)
    ?.type ?? null) as string | null;
  const tournamentCreatedById =
    (tournament as { createdById?: string | null } | null)?.createdById ?? null;
  const isOrganizer =
    Boolean(userProfile?.id) &&
    Boolean(tournamentCreatedById) &&
    userProfile?.id === tournamentCreatedById;

  const userRole = resolveMatchUserRole({
    userId: userProfile?.id ?? null,
    teamACaptainId,
    teamBCaptainId,
    teamAMemberIds,
    teamBMemberIds,
    isOrganizer,
  });
  const isPlaying = userRole === "captain" || userRole === "participant";

  const panels = getMatchPanels({
    mode: tournament?.manualWinnerMode,
    status: match?.status,
    isCaptain,
    isTauriHere,
    isPlaying,
    isOrganizer,
  });

  const checklistSteps = useMemo(
    () =>
      getMatchChecklist({
        match: match ?? null,
        tournamentType,
        role: userRole,
        isInTauri: isTauriHere,
        lcuConnected: Boolean(lcuStatus?.connected),
      }),
    [match, tournamentType, userRole, isTauriHere, lcuStatus?.connected],
  );

  const deadlineUtc = useMemo(() => {
    const s = match?.startByDeadlineUtc;
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  }, [match?.startByDeadlineUtc]);

  const [minutesLeft, setMinutesLeft] = useState<number | null>(null);
  useEffect(() => {
    if (!deadlineUtc || matchCompleted || match?.isTechnicalWin) {
      setMinutesLeft(null);
      return;
    }
    const update = () => {
      const now = new Date();
      setMinutesLeft(
        now >= deadlineUtc
          ? 0
          : Math.max(
              0,
              Math.ceil((deadlineUtc.getTime() - now.getTime()) / 60_000),
            ),
      );
    };
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [deadlineUtc, matchCompleted, match?.isTechnicalWin]);

  if (!match) {
    return (
      <div className={cn("space-y-4", className)}>
        <Skeleton className="h-5 w-40" />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-44 w-full" />
        </div>
      </div>
    );
  }

  const scoreA = match.scoreA ?? 0;
  const scoreB = match.scoreB ?? 0;
  const showScore = matchCompleted || scoreA > 0 || scoreB > 0;

  // Диспут заводит только участник матча — то же правило, что на бэкенде
  // (и что написано в самой форме). Анониму и зрителю форма показывалась зря:
  // подать они всё равно не могут, а блок занимал экран сразу под составами.
  const canFileDispute =
    isPlaying &&
    !disputes.some((d) => d.status === "Open" || d.status === "InReview");
  // Заголовок блока без единого диспута и без права подать — пустая рамка.
  const showDisputes =
    matchCompleted && (disputes.length > 0 || canFileDispute);

  // Имя раунда синтезирует адаптер сетки («Четвертьфинал · Матч 2») — берём его,
  // чтобы сетка и страница матча называли матч одинаково. Свой `roundIndex`
  // показывать нельзя: он 0-based, и в шапке это читалось как «раунд 0».
  const roundLabel = match.name ?? tUi("litMatchOverview.raund", { n: (match.roundIndex ?? 0) + 1 });

  // Половину сетки называем только там, где их две: `bracket` заполняется лишь
  // для double elimination, в обычной сетке «Верхняя сетка» — пустой шум.
  const bracketLabel =
    match.bracket === "losers"
      ? tUi("litMatchOverview.nizhnyayaSetka")
      : match.bracket === "finals"
        ? tUi("litMatchOverview.grandFinal")
        : match.bracket === "winners"
          ? tUi("litMatchOverview.verhnyayaSetka")
          : null;

  const deadlineText = deadlineUtc
    ? formatTimeOnlyMsk(deadlineUtc, {
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <div className={cn("space-y-8", className)}>
      {/* Шапка: метаданные одной строкой. Иерархия — типографикой, без рамок и
          отдельных карточек на каждый факт. */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm text-muted-foreground">
        <Badge variant={matchStatusBadgeVariant(match.status)}>
          {matchStatusLabel(match.status)}
        </Badge>
        <span>Bo{match.bestOf ?? 1}</span>
        <span aria-hidden>·</span>
        <span>{roundLabel}</span>
        {bracketLabel && (
          <>
            <span aria-hidden>·</span>
            <span>{bracketLabel}</span>
          </>
        )}
        {match.isTechnicalWin && (
          <>
            <span aria-hidden>·</span>
            <span>{t("technicalWinTitle")}</span>
          </>
        )}
        {/* Счёт и статистика приезжают разными путями: счёт — из согласия капитанов,
            статистика — только из реплея. Матч, закрытый согласием без реплея, выглядел
            как полноценный, и дыра всплывала в профилях игроков через сутки. */}
        {match.hasStats === false && (
          <>
            <span aria-hidden>·</span>
            <Badge variant="outline" title={t("noStatsHint")}>
              {t("noStatsBadge")}
            </Badge>
          </>
        )}
        {apiBracket?.matchStartDeadlineEnabled &&
          deadlineText &&
          !matchCompleted &&
          !match.isTechnicalWin && (
            <>
              <span aria-hidden>·</span>
              <span>
                {t("deadlineBy", { time: deadlineText })}
                {minutesLeft !== null && minutesLeft > 0
                  ? ` (${t("minutesLeft", { count: minutesLeft })})`
                  : minutesLeft === 0
                    ? ` — ${t("deadlinePassed")}`
                    : ""}
              </span>
            </>
          )}
      </div>

      {/* Кто против кого */}
      <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-[1fr_auto_1fr]">
        <TeamLineup
          fallbackName={bracketTeamA?.name ?? tUi("litMatchOverview.budetOpredeleno")}
          team={teamA}
        />

        {/* Без order-*: в одну колонку (мобильный) блок встаёт между составами,
            как и в DOM, — «vs» над обеими командами читалось как заголовок. */}
        <div className="flex items-center justify-center py-1 sm:py-0 sm:pt-3">
          {showScore ? (
            <span className="text-3xl font-semibold tabular-nums">
              {scoreA}
              <span className="px-2 text-muted-foreground">:</span>
              {scoreB}
            </span>
          ) : (
            <span className="text-sm font-medium uppercase tracking-widest text-muted-foreground">
              vs
            </span>
          )}
        </div>

        <TeamLineup
          fallbackName={bracketTeamB?.name ?? tUi("litMatchOverview.budetOpredeleno")}
          team={teamB}
          mirror
        />
      </div>

      {/* KAN-1676: пулы RandomDraft — одно место на всю фичу, сразу под составами.
          Секция сама скрывается у обычных турниров без пулов. */}
      <MatchChampionPoolsSection
        tournamentId={tournamentId}
        matchId={matchId}
        bestOf={match?.bestOf ?? 1}
        isFinal={match?.bracket === "finals"}
        canPublish={canManagePools}
        matchCompleted={matchCompleted}
      />

      {/* Дальше — только то, что относится к смотрящему прямо сейчас. */}
      <MatchPreDraftCard
        match={match}
        tournamentType={tournamentType}
        role={userRole}
        side={userSide}
        matchId={matchId}
      />

      {/* KAN-821: драфт переживает матч — блок сам прячется, если драфта не было. */}
      <MatchDraftResult
        matchId={matchId}
        blueName={teamA?.name ?? bracketTeamA?.name}
        redName={teamB?.name ?? bracketTeamB?.name}
      />

      {(match.bestOf === 3 || match.bestOf === 5) && (
        <MatchSideVetoBlock matchId={matchId} />
      )}

      {panels.replayPending && (
        <div className="rounded-lg border border-border bg-muted/30 p-4">
          <p className="text-sm font-medium text-foreground">
            {tUi("matchOverview.rezultatPodtverzhdaetsyaRepleem")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {tUi("matchOverview.zagruzit")} <code>.rofl</code> можно, когда матч начнётся. Матчи
            раунда стартуют автоматически; если ваш назначен на более позднее
            время, дождитесь его. Как только матч пойдёт, здесь появится
            загрузка — страницу обновлять не нужно.
          </p>
        </div>
      )}

      {panels.replayConfirm && (
        <ReplayConfirmCard
          matchId={matchId}
          bestOf={match.bestOf ?? 1}
          onConfirmed={() => {
            void refetchBracket();
          }}
        />
      )}

      {/* Запасной путь вне режима replay: Discord отклоняет реплеи тяжелее ~25 МБ,
          а без этой кнопки капитану (и организатору) было некуда деть файл, кроме
          как писать организатору вручную — один матч так и остался без статистики. */}
      {panels.replayUpload && (
        <ReplayConfirmCard
          matchId={matchId}
          bestOf={match.bestOf ?? 1}
          variant="inline"
          title={tUi("matchOverview.zagruzitRepley")}
          onConfirmed={() => {
            void refetchBracket();
          }}
        />
      )}

      {panels.captainResult &&
        match.teamAId &&
        match.teamBId &&
        bracketTeamA &&
        bracketTeamB && (
          <CaptainResultCard
            matchId={matchId}
            bestOf={match.bestOf ?? 1}
            teamA={{ id: match.teamAId, name: bracketTeamA.name }}
            teamB={{ id: match.teamBId, name: bracketTeamB.name }}
            myTeamId={userSide === "blue" ? match.teamAId : match.teamBId}
            pendingResult={match.pendingResult}
            onDone={() => {
              void refetchBracket();
            }}
          />
        )}

      {showDisputes && (
        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-foreground">
              {tDispute("disputesBlockTitle")}
            </h2>
            <p className="text-sm text-muted-foreground">
              {tDispute("disputesBlockDescription")}
            </p>
          </div>
          {disputes.length > 0 && (
            <ul className="space-y-2">
              {disputes.map((d) => (
                <li
                  key={d.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-border p-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">
                      {d.teamName ?? tUi("litMatchOverview.komanda")}
                    </p>
                    <p className="line-clamp-1 text-sm text-muted-foreground">
                      {d.reason}
                    </p>
                  </div>
                  <DisputeStatusBadge status={d.status} />
                </li>
              ))}
            </ul>
          )}
          {canFileDispute && (
            <CreateDisputeForm
              matchId={matchId}
              tournamentId={tournamentId}
              match={match}
              teams={apiBracket?.teams ?? []}
            />
          )}
        </section>
      )}

      {/* KAN-792: разбор игры для всех, кто открыл матч из сетки. Раньше эти три
          блока жили только в организаторской правке сетки (MatchStatsModal), и игрок
          не мог посмотреть, как сыграли в его собственном матче. Каждый прячет себя
          сам, если данных нет, поэтому обёртки-условия здесь не нужно. */}
      <MatchGameTeamStats matchId={matchId} />
      {/* KAN-770: стиль обеих команд рядом — база сравнения команды турнира. */}
      <MatchTeamStyles
        tournamentId={tournamentId}
        tournamentType={tournamentType}
        teamA={{
          name: teamA?.name ?? bracketTeamA?.name ?? "—",
          ids: [match.teamAId, teamA?.id, teamA?.draftTeamId],
        }}
        teamB={{
          name: teamB?.name ?? bracketTeamB?.name ?? "—",
          ids: [match.teamBId, teamB?.id, teamB?.draftTeamId],
        }}
      />
      {/* KAN-814: пары по ролям — ТРЕТЬИМ блоком под составами, а не отдельной
          витриной выше. Место согласовано с автором разбора: к этому моменту уже
          видно, кто на какой роли и с какими числами, и сравнение читается как
          продолжение таблицы, а не как самостоятельный экран. */}
      <MatchLaneMatchupsSection matchId={matchId} />
      <MatchBehaviorProfile matchId={matchId} />
      <MatchTimelineFeed matchId={matchId} />

      {panels.downloadApp && <DownloadAppBanner variant="match-critical" />}

      {/* Десктоп-часть: монтируем только внутри приложения.
          Раньше эти блоки висели в дереве и в вебе, где они бесполезны:
          — LcuStatusPanel сам рисовал null (`!checkIsTauri()`), но его хуки
            отрабатывали ДО этого return и вхолостую дёргали /matches/{id}/players
            и /me/desktop-token (проверено в браузере: оба ушли анониму в 401);
          — MatchLiveBoard на матче в Live поллил /brackets/matches/{id}/live-data
            раз в 5 с и держал SignalR-подписку, хотя данные туда пушит только
            Tauri, — в вебе борд гарантированно пустой и читается как поломка. */}
      {isTauriHere && (
        <>
          {panels.liveBoard && (
            <MatchLiveBoard
              matchId={matchId}
              team1Name={teamA?.name ?? bracketTeamA?.name}
              team2Name={teamB?.name ?? bracketTeamB?.name}
              myGameName={userProfile?.displayName ?? undefined}
            />
          )}

          <LcuStatusPanel
            tournamentId={tournamentId}
            matchId={matchId}
            bestOf={match.bestOf ?? 1}
            tournamentType={tournamentType ?? ""}
            matchStatus={match.status}
            teamAId={match.teamAId}
            teamBId={match.teamBId}
          />

          {panels.checklist && <MatchChecklist steps={checklistSteps} />}
        </>
      )}

      {isPlaying && (
        <p className="text-sm">
          <Link href="/help" className="text-primary hover:underline">
            {t("helpLink")}
          </Link>
        </p>
      )}
    </div>
  );
}
